import { useState, useEffect } from 'react';
import {
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
  CheckIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import I18nField from './I18nField';
import { createI18nObject, getLangLabel } from '../utils/i18n';

const TABS = [
  { id: 'general', label: 'General' },
  { id: 'costs', label: 'Cost Parameters' },
  { id: 'extras', label: 'Extra Services' },
  { id: 'discounts', label: 'Bulk Discounts' },
  { id: 'timeline', label: 'Timeline Settings' },
  { id: 'farm', label: 'Farm Config' },
  { id: 'packing', label: 'Packing' },
  { id: 'stats', label: 'Farm Stats' },
];

const SEGMENT_OPTIONS = ['PER_MONTH', 'PER_HOUR', 'PER_PRINT', 'PER_KG', 'PER_QUOTE', 'PER_UNIT', 'PER_CM3', 'PERCENTAGE'];
const CATEGORY_OPTIONS = ['OPERATIONAL', 'FACILITY', 'LABOR', 'UTILITIES', 'CONSUMABLES', 'MAINTENANCE', 'OTHER'];
const SERVICE_TYPE_OPTIONS = ['MODEL_REVIEW', 'POST_PROCESSING', 'ASSEMBLY', 'PACKAGING', 'EXPRESS_SHIPPING', 'CUSTOM'];
const CHARGE_TYPE_OPTIONS = ['PER_OBJECT', 'PER_PLATE', 'PER_HOUR', 'FIXED', 'PERCENTAGE'];
const PER_HOUR_BASE_REFERENCE_OPTIONS = ['PRINT_TIME_PERCENTAGE', 'MODEL_TIME_PERCENTAGE', 'SUPPORT_TIME_PERCENTAGE'];
const BULK_APPLY_TO_OPTIONS = ['SUBTOTAL', 'MATERIAL_ONLY', 'OPERATIONAL_ONLY', 'TOTAL_BEFORE_PROFIT'];
const TIMELINE_APPLY_TO_OPTIONS = ['SUBTOTAL', 'TOTAL_BEFORE_VAT', 'TOTAL_WITH_VAT'];
const SEQUENCE_MODE_OPTIONS = ['BATCH', 'KIT'];

export default function FarmSettingsForm({ settings, onSave, onReset, onDelete }) {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [availableLanguages, setAvailableLanguages] = useState([]);
  const [formData, setFormData] = useState({
    baseProfitMargin: 30,
    minimumQuoteValue: 10,
    operatingCountries: [{ countryCode: 'PT', isDefault: true }],
    supportedLanguages: user?.supportedLanguages || ['pt'],
    costParameters: [],
    extraServices: [],
    bulkDiscounts: {
      enabled: true,
      minimumOrderValue: 0,
      tiers: []
    },
    timelineSettings: {
      earliestReadyDays: 1,
      applyTo: 'SUBTOTAL',
      tiers: []
    },
    currency: {
      code: 'EUR',
      symbol: '€',
      symbolPosition: 'after'
    },
    quoteValidity: {
      days: 30,
      notifyBeforeExpiry: true,
      notifyDaysBefore: 7
    },
    farmConfig: {
      businessDayStart: '08:00',
      businessDayEnd: '18:00',
      concurrencyCapPercentage: 50,
      defaultSequenceMode: 'BATCH',
      timezone: 'Europe/Lisbon'
    },
    packingConfig: {
      wrappingLossPercent: 12,
      errorMarginPercent: 12,
      weightSafetyMarginPercent: 5,
      paddingPerItemMm: 5,
      optimizationTarget: 'lowest_cost',
      allowMixedBoxSizes: true,
      tightFitThreshold: 0.88,
      fillerWeightGrams: 75,
    }
  });
  const [saving, setSaving] = useState(false);
  const [materials, setMaterials] = useState([]);
  const [materialsLoading, setMaterialsLoading] = useState(true);

  const supportedLanguages = formData.supportedLanguages || ['pt'];

  useEffect(() => {
    if (settings) {
      const { availableLanguages: avail, ...rest } = settings;
      if (avail) setAvailableLanguages(avail);
      setFormData(rest);
    }
  }, [settings]);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      setMaterialsLoading(true);
      const response = await api.get('/auth/materials');
      setMaterials(response.data.data || []);
    } catch (err) {
      console.error('Failed to fetch materials:', err);
    } finally {
      setMaterialsLoading(false);
    }
  };

  // Helper function to clean MongoDB metadata from objects
  const cleanData = (data) => {
    if (Array.isArray(data)) {
      return data.map(item => cleanData(item));
    } else if (data !== null && typeof data === 'object') {
      const cleaned = { ...data };
      delete cleaned._id;
      delete cleaned.id;
      delete cleaned.__v;
      delete cleaned.createdAt;
      delete cleaned.updatedAt;

      Object.keys(cleaned).forEach(key => {
        if (cleaned[key] !== null && typeof cleaned[key] === 'object') {
          cleaned[key] = cleanData(cleaned[key]);
        }
      });

      return cleaned;
    }
    return data;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanedData = cleanData(formData);

      // Remove virtual/read-only fields
      delete cleanedData.activeCostParameters;
      delete cleanedData.activeDiscountTiers;
      delete cleanedData.activeExtraServices;
      delete cleanedData.activeTimelineTiers;
      delete cleanedData.farmStats;
      delete cleanedData.farm;
      delete cleanedData.availableLanguages;

      await onSave(cleanedData);
    } finally {
      setSaving(false);
    }
  };

  // Cost Parameters
  const addCostParameter = () => {
    setFormData({
      ...formData,
      costParameters: [
        ...formData.costParameters,
        {
          name: '',
          description: '',
          segment: 'PER_HOUR',
          category: 'OTHER',
          value: 0,
          isActive: true,
          order: formData.costParameters.length
        }
      ]
    });
  };

  const updateCostParameter = (index, field, value) => {
    const updated = [...formData.costParameters];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, costParameters: updated });
  };

  const removeCostParameter = (index) => {
    setFormData({
      ...formData,
      costParameters: formData.costParameters.filter((_, i) => i !== index)
    });
  };

  // Extra Services
  const addExtraService = () => {
    setFormData({
      ...formData,
      extraServices: [
        ...formData.extraServices,
        {
          key: '',
          type: 'CUSTOM',
          name: createI18nObject(supportedLanguages),
          description: createI18nObject(supportedLanguages),
          chargeType: 'FIXED',
          value: 0,
          minimumCharge: 0,
          multiplyByQuantity: false,
          perHourSettings: null,
          includeMarginInPercentage: true,
          defaultChecked: false,
          chargeByDefault: false,
          enabled: true,
          isActive: true,
          order: formData.extraServices.length,
          applicableMaterials: [],

          promotion: {
            isActive: false,
            promotionalValue: null,
            startDate: null,
            endDate: null,
            description: createI18nObject(supportedLanguages)
          }
        }
      ]
    });
  };

  const updateExtraService = (index, field, value) => {
    const updated = [...formData.extraServices];
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, extraServices: updated });
  };

  const updateExtraServiceI18n = (index, field, lang, value) => {
    const updated = [...formData.extraServices];
    if (!updated[index][field] || typeof updated[index][field] === 'string') {
      updated[index][field] = createI18nObject(supportedLanguages);
    }
    updated[index] = {
      ...updated[index],
      [field]: { ...updated[index][field], [lang]: value || null }
    };
    setFormData({ ...formData, extraServices: updated });
  };

  const updateExtraServicePromotion = (index, field, value) => {
    const updated = [...formData.extraServices];
    if (!updated[index].promotion) {
      updated[index].promotion = {
        isActive: false,
        promotionalValue: null,
        startDate: null,
        endDate: null,
        description: createI18nObject(supportedLanguages)
      };
    }
    updated[index] = {
      ...updated[index],
      promotion: { ...updated[index].promotion, [field]: value }
    };
    setFormData({ ...formData, extraServices: updated });
  };

  const updateExtraServicePromotionI18n = (index, field, lang, value) => {
    const updated = [...formData.extraServices];
    if (!updated[index].promotion) {
      updated[index].promotion = {
        isActive: false,
        promotionalValue: null,
        startDate: null,
        endDate: null,
        description: createI18nObject(supportedLanguages)
      };
    }
    if (!updated[index].promotion[field] || typeof updated[index].promotion[field] === 'string') {
      updated[index].promotion[field] = createI18nObject(supportedLanguages);
    }
    updated[index] = {
      ...updated[index],
      promotion: {
        ...updated[index].promotion,
        [field]: { ...updated[index].promotion[field], [lang]: value }
      }
    };
    setFormData({ ...formData, extraServices: updated });
  };

  const removeExtraService = (index) => {
    setFormData({
      ...formData,
      extraServices: formData.extraServices.filter((_, i) => i !== index)
    });
  };

  // Bulk Discount Tiers
  const addBulkDiscountTier = () => {
    setFormData({
      ...formData,
      bulkDiscounts: {
        ...formData.bulkDiscounts,
        tiers: [
          ...formData.bulkDiscounts.tiers,
          {
            key: '',
            name: createI18nObject(supportedLanguages),
            minQuantity: 1,
            maxQuantity: null,
            discountPercentage: 0,
            isActive: true,
            applyTo: 'SUBTOTAL',
            description: createI18nObject(supportedLanguages)
          }
        ]
      }
    });
  };

  const updateBulkDiscountTier = (index, field, value) => {
    const updated = [...formData.bulkDiscounts.tiers];
    updated[index] = { ...updated[index], [field]: value === '' ? null : value };
    setFormData({
      ...formData,
      bulkDiscounts: { ...formData.bulkDiscounts, tiers: updated }
    });
  };

  const updateBulkDiscountTierI18n = (index, field, lang, value) => {
    const updated = [...formData.bulkDiscounts.tiers];
    if (!updated[index][field] || typeof updated[index][field] === 'string') {
      updated[index][field] = createI18nObject(supportedLanguages);
    }
    updated[index] = {
      ...updated[index],
      [field]: { ...updated[index][field], [lang]: value || null }
    };
    setFormData({
      ...formData,
      bulkDiscounts: { ...formData.bulkDiscounts, tiers: updated }
    });
  };

  const removeBulkDiscountTier = (index) => {
    setFormData({
      ...formData,
      bulkDiscounts: {
        ...formData.bulkDiscounts,
        tiers: formData.bulkDiscounts.tiers.filter((_, i) => i !== index)
      }
    });
  };

  // Timeline Tiers
  const addTimelineTier = () => {
    setFormData({
      ...formData,
      timelineSettings: {
        ...formData.timelineSettings,
        tiers: [
          ...formData.timelineSettings.tiers,
          {
            key: '',
            name: createI18nObject(supportedLanguages),
            daysAfterEarliest: 0,
            feePercentage: 0,
            minimumFee: 0,
            description: createI18nObject(supportedLanguages),
            isActive: true,
            isDefault: false
          }
        ]
      }
    });
  };

  const updateTimelineTier = (index, field, value) => {
    const updated = [...formData.timelineSettings.tiers];
    if (field === 'isDefault' && value === true) {
      updated.forEach((tier, i) => {
        if (i !== index) tier.isDefault = false;
      });
    }
    updated[index] = { ...updated[index], [field]: value === '' ? null : value };
    setFormData({
      ...formData,
      timelineSettings: { ...formData.timelineSettings, tiers: updated }
    });
  };

  const updateTimelineTierI18n = (index, field, lang, value) => {
    const updated = [...formData.timelineSettings.tiers];
    if (!updated[index][field] || typeof updated[index][field] === 'string') {
      updated[index][field] = createI18nObject(supportedLanguages);
    }
    updated[index] = {
      ...updated[index],
      [field]: { ...updated[index][field], [lang]: value || null }
    };
    setFormData({
      ...formData,
      timelineSettings: { ...formData.timelineSettings, tiers: updated }
    });
  };

  const removeTimelineTier = (index) => {
    setFormData({
      ...formData,
      timelineSettings: {
        ...formData.timelineSettings,
        tiers: formData.timelineSettings.tiers.filter((_, i) => i !== index)
      }
    });
  };

  // Operating Countries
  const addOperatingCountry = () => {
    setFormData({
      ...formData,
      operatingCountries: [
        ...(formData.operatingCountries || []),
        { countryCode: '', isDefault: false }
      ]
    });
  };

  const updateOperatingCountry = (index, field, value) => {
    const updated = [...(formData.operatingCountries || [])];
    if (field === 'isDefault' && value === true) {
      updated.forEach((c, i) => { if (i !== index) c.isDefault = false; });
    }
    updated[index] = { ...updated[index], [field]: value };
    setFormData({ ...formData, operatingCountries: updated });
  };

  const removeOperatingCountry = (index) => {
    setFormData({
      ...formData,
      operatingCountries: (formData.operatingCountries || []).filter((_, i) => i !== index)
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {/* Header */}
      <div className="bg-white shadow-sm rounded-lg p-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Farm Settings</h1>
            <p className="text-sm text-gray-600 mt-1">Configure your quoting parameters and business rules</p>
          </div>
          <div className="flex space-x-3">
            {onReset && (
              <button
                type="button"
                onClick={onReset}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
              >
                <ArrowPathIcon className="h-5 w-5 mr-2" />
                Reset to Defaults
              </button>
            )}
            {onDelete && (
              <button
                type="button"
                onClick={onDelete}
                className="px-4 py-2 border border-red-300 rounded-md text-sm font-medium text-red-700 hover:bg-red-50 flex items-center"
              >
                <TrashIcon className="h-5 w-5 mr-2" />
                Delete Settings
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white shadow-sm rounded-lg">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px overflow-x-auto">
            {TABS.map((tab) => (
              <button
                key={tab.id}
                type="button"
                onClick={() => setActiveTab(tab.id)}
                className={`whitespace-nowrap py-4 px-6 border-b-2 font-medium text-sm ${activeTab === tab.id
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
              >
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* General Tab */}
          {activeTab === 'general' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Base Profit Margin (%)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="500"
                    step="0.1"
                    value={formData.baseProfitMargin}
                    onChange={(e) => setFormData({ ...formData, baseProfitMargin: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                  <p className="text-xs text-gray-500 mt-1">Default profit margin percentage (0-500)</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Quote Value</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={formData.minimumQuoteValue}
                    onChange={(e) => setFormData({ ...formData, minimumQuoteValue: Number(e.target.value) })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              {/* Currency */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Currency</h3>
                <div className="grid grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Code</label>
                    <input
                      type="text"
                      maxLength="3"
                      value={formData.currency?.code || 'EUR'}
                      onChange={(e) => setFormData({
                        ...formData,
                        currency: { ...formData.currency, code: e.target.value.toUpperCase() }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <p className="text-xs text-gray-500 mt-1">ISO currency code (max 3 chars)</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Symbol</label>
                    <input
                      type="text"
                      maxLength="5"
                      value={formData.currency?.symbol || '€'}
                      onChange={(e) => setFormData({
                        ...formData,
                        currency: { ...formData.currency, symbol: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Symbol Position</label>
                    <select
                      value={formData.currency?.symbolPosition || 'after'}
                      onChange={(e) => setFormData({
                        ...formData,
                        currency: { ...formData.currency, symbolPosition: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="before">Before</option>
                      <option value="after">After</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Quote Validity */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-4">Quote Validity</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Validity Days
                    </label>
                    <input
                      type="number"
                      min="1"
                      max="365"
                      value={formData.quoteValidity?.days || 30}
                      onChange={(e) => setFormData({
                        ...formData,
                        quoteValidity: { ...formData.quoteValidity, days: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>

                  <div className="col-span-2 space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={formData.quoteValidity?.notifyBeforeExpiry ?? true}
                        onChange={(e) => setFormData({
                          ...formData,
                          quoteValidity: { ...formData.quoteValidity, notifyBeforeExpiry: e.target.checked }
                        })}
                        className="h-4 w-4 text-blue-600 rounded"
                      />
                      <span className="ml-2 text-sm text-gray-700">Notify Before Expiry</span>
                    </label>

                    {formData.quoteValidity?.notifyBeforeExpiry && (
                      <div className="ml-6">
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Notify Days Before
                        </label>
                        <input
                          type="number"
                          min="1"
                          value={formData.quoteValidity?.notifyDaysBefore || 7}
                          onChange={(e) => setFormData({
                            ...formData,
                            quoteValidity: { ...formData.quoteValidity, notifyDaysBefore: Number(e.target.value) }
                          })}
                          className="w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Operating Countries */}
              <div className="border-t pt-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-medium text-gray-900">Operating Countries</h3>
                  <button
                    type="button"
                    onClick={addOperatingCountry}
                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center text-sm"
                  >
                    <PlusIcon className="h-4 w-4 mr-1" />
                    Add Country
                  </button>
                </div>
                {(formData.operatingCountries || []).length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No operating countries defined</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {(formData.operatingCountries || []).map((country, index) => (
                      <div key={index} className="flex items-center gap-4 border border-gray-200 rounded-lg p-3 bg-gray-50">
                        <div className="flex-1">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Country Code (ISO 2-letter)</label>
                          <input
                            type="text"
                            maxLength="2"
                            value={country.countryCode || ''}
                            onChange={(e) => updateOperatingCountry(index, 'countryCode', e.target.value.toUpperCase())}
                            className="w-24 px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="PT"
                            required
                          />
                        </div>
                        <label className="flex items-center">
                          <input
                            type="radio"
                            name="defaultCountry"
                            checked={country.isDefault}
                            onChange={() => updateOperatingCountry(index, 'isDefault', true)}
                            className="h-4 w-4 text-blue-600"
                          />
                          <span className="ml-2 text-sm text-gray-700">Default</span>
                        </label>
                        <button
                          type="button"
                          onClick={() => removeOperatingCountry(index)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <TrashIcon className="h-5 w-5" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Supported Languages */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium text-gray-900 mb-2">Supported Languages</h3>
                <p className="text-sm text-gray-500 mb-4">
                  Select which languages your farm supports. The first language is the default (required) language.
                </p>

                {availableLanguages.length === 0 ? (
                  <div className="text-center py-8 bg-gray-50 rounded-lg">
                    <p className="text-gray-500">No available languages returned from server</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {availableLanguages.map((lang) => {
                      const isSupported = supportedLanguages.includes(lang);
                      const isDefault = supportedLanguages[0] === lang;
                      const isOnlyLanguage = isSupported && supportedLanguages.length === 1;

                      return (
                        <div
                          key={lang}
                          className={`flex items-center justify-between border rounded-lg p-3 ${isDefault ? 'border-blue-300 bg-blue-50' : isSupported ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'}`}
                        >
                          <div className="flex items-center gap-3">
                            <input
                              type="checkbox"
                              checked={isSupported}
                              disabled={isOnlyLanguage}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setFormData({
                                    ...formData,
                                    supportedLanguages: [...supportedLanguages, lang]
                                  });
                                } else {
                                  const filtered = supportedLanguages.filter(l => l !== lang);
                                  setFormData({
                                    ...formData,
                                    supportedLanguages: filtered
                                  });
                                }
                              }}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="text-sm font-medium text-gray-900">{getLangLabel(lang)}</span>
                            <span className="text-xs text-gray-500">({lang})</span>
                            {isDefault && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                                Default
                              </span>
                            )}
                          </div>

                          {isSupported && !isDefault && (
                            <button
                              type="button"
                              onClick={() => {
                                const others = supportedLanguages.filter(l => l !== lang);
                                setFormData({
                                  ...formData,
                                  supportedLanguages: [lang, ...others]
                                });
                              }}
                              className="text-xs px-2 py-1 text-blue-600 hover:text-blue-800 hover:bg-blue-100 rounded"
                            >
                              Set as Default
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Cost Parameters Tab */}
          {activeTab === 'costs' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Cost Parameters</h3>
                <button
                  type="button"
                  onClick={addCostParameter}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Cost Parameter
                </button>
              </div>

              {formData.costParameters.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No cost parameters defined</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.costParameters.map((param, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Name *</label>
                          <input
                            type="text"
                            value={param.name}
                            onChange={(e) => updateCostParameter(index, 'name', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={param.value}
                            onChange={(e) => updateCostParameter(index, 'value', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Segment *</label>
                          <select
                            value={param.segment}
                            onChange={(e) => updateCostParameter(index, 'segment', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            {SEGMENT_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                          <select
                            value={param.category}
                            onChange={(e) => updateCostParameter(index, 'category', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            {CATEGORY_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                          <input
                            type="text"
                            value={param.description}
                            onChange={(e) => updateCostParameter(index, 'description', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>

                        <div className="col-span-2 flex justify-between items-center">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={param.isActive}
                              onChange={(e) => updateCostParameter(index, 'isActive', e.target.checked)}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Active</span>
                          </label>

                          <button
                            type="button"
                            onClick={() => removeCostParameter(index)}
                            className="text-red-600 hover:text-red-800 flex items-center"
                          >
                            <TrashIcon className="h-5 w-5 mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Extra Services Tab */}
          {activeTab === 'extras' && (
            <div className="space-y-4">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Extra Services</h3>
                <button
                  type="button"
                  onClick={addExtraService}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Service
                </button>
              </div>

              {formData.extraServices.length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No extra services defined</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.extraServices.map((service, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Key *
                            <span className="text-xs text-gray-500 ml-1">(unique identifier, max 100 chars)</span>
                          </label>
                          <input
                            type="text"
                            maxLength="100"
                            value={service.key || ''}
                            onChange={(e) => updateExtraService(index, 'key', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="e.g., model_review"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Type *</label>
                          <select
                            value={service.type}
                            onChange={(e) => updateExtraService(index, 'type', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            {SERVICE_TYPE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        <div className="col-span-2">
                          <I18nField
                            label="Name"
                            value={typeof service.name === 'object' ? service.name : {}}
                            onChange={(lang, value) => updateExtraServiceI18n(index, 'name', lang, value)}
                            languages={supportedLanguages}
                            required
                            maxLength={100}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Charge Type *</label>
                          <select
                            value={service.chargeType}
                            onChange={(e) => updateExtraService(index, 'chargeType', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          >
                            {CHARGE_TYPE_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Value *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={service.value}
                            onChange={(e) => updateExtraService(index, 'value', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Charge</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={service.minimumCharge}
                            onChange={(e) => updateExtraService(index, 'minimumCharge', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>

                        {/* multiplyByQuantity - only for PER_OBJECT, PER_PLATE, PER_HOUR */}
                        {['PER_OBJECT', 'PER_PLATE', 'PER_HOUR'].includes(service.chargeType) && (
                          <div className="col-span-2">
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.multiplyByQuantity || false}
                                onChange={(e) => updateExtraService(index, 'multiplyByQuantity', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Multiply by Quantity</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">Multiply the service cost by the order quantity</p>
                          </div>
                        )}

                        {/* perHourSettings - only for PER_HOUR */}
                        {service.chargeType === 'PER_HOUR' && (
                          <div className="col-span-2 bg-yellow-50 p-4 rounded-md">
                            <h4 className="text-sm font-medium text-gray-700 mb-3">Per Hour Settings</h4>
                            <p className="text-xs text-gray-500 mb-3">Configure which time reference to use. Leave empty to use total print time.</p>
                            <div className="grid grid-cols-2 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Base Reference</label>
                                <select
                                  value={service.perHourSettings?.baseReference || ''}
                                  onChange={(e) => {
                                    if (e.target.value) {
                                      updateExtraService(index, 'perHourSettings', {
                                        ...service.perHourSettings,
                                        baseReference: e.target.value,
                                        percentage: service.perHourSettings?.percentage ?? 100
                                      });
                                    } else {
                                      updateExtraService(index, 'perHourSettings', null);
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                >
                                  <option value="">None (use total print time)</option>
                                  {PER_HOUR_BASE_REFERENCE_OPTIONS.map(opt => (
                                    <option key={opt} value={opt}>{opt}</option>
                                  ))}
                                </select>
                              </div>

                              {service.perHourSettings?.baseReference && (
                                <div>
                                  <label className="block text-sm font-medium text-gray-700 mb-1">Percentage (0-100)</label>
                                  <input
                                    type="number"
                                    min="0"
                                    max="100"
                                    step="1"
                                    value={service.perHourSettings?.percentage ?? 100}
                                    onChange={(e) => updateExtraService(index, 'perHourSettings', {
                                      ...service.perHourSettings,
                                      percentage: Number(e.target.value)
                                    })}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  />
                                  <p className="text-xs text-gray-500 mt-1">Percentage of the base time to use</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}

                        {/* includeMarginInPercentage - only for PERCENTAGE */}
                        {service.chargeType === 'PERCENTAGE' && (
                          <div className="col-span-2">
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.includeMarginInPercentage ?? true}
                                onChange={(e) => updateExtraService(index, 'includeMarginInPercentage', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Include Profit Margin in Percentage Base</span>
                            </label>
                            <p className="text-xs text-gray-500 mt-1 ml-6">When enabled, the percentage is calculated on the subtotal including profit margin</p>
                          </div>
                        )}

                        <div className="col-span-2">
                          <I18nField
                            label="Description"
                            value={typeof service.description === 'object' ? service.description : {}}
                            onChange={(lang, value) => updateExtraServiceI18n(index, 'description', lang, value)}
                            languages={supportedLanguages}
                            maxLength={500}
                          />
                        </div>

                        <div className="col-span-2">
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Applicable Materials
                            <span className="text-xs text-gray-500 ml-1">(empty = all materials)</span>
                          </label>

                          <div className="mb-2 min-h-[2.5rem] flex flex-wrap gap-2 p-2 border border-gray-300 rounded-md bg-gray-50">
                            {service.applicableMaterials && service.applicableMaterials.length > 0 ? (
                              service.applicableMaterials.map((materialId) => {
                                const material = materials.find(m => m._id === materialId);
                                return (
                                  <span
                                    key={materialId}
                                    className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 text-sm rounded-md"
                                  >
                                    <span>{material ? material.name : materialId}</span>
                                    <button
                                      type="button"
                                      onClick={() => {
                                        const updated = service.applicableMaterials.filter(id => id !== materialId);
                                        updateExtraService(index, 'applicableMaterials', updated);
                                      }}
                                      className="hover:bg-blue-200 rounded"
                                    >
                                      <XMarkIcon className="h-4 w-4" />
                                    </button>
                                  </span>
                                );
                              })
                            ) : (
                              <span className="text-gray-400 text-sm">No materials selected - service applies to all materials</span>
                            )}
                          </div>

                          {materialsLoading ? (
                            <p className="text-sm text-gray-500">Loading materials...</p>
                          ) : (
                            <select
                              value=""
                              onChange={(e) => {
                                if (e.target.value) {
                                  const materialId = e.target.value;
                                  const currentMaterials = service.applicableMaterials || [];
                                  if (!currentMaterials.includes(materialId)) {
                                    updateExtraService(index, 'applicableMaterials', [...currentMaterials, materialId]);
                                  }
                                }
                              }}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            >
                              <option value="">+ Add Material</option>
                              {materials
                                .filter(m => !service.applicableMaterials?.includes(m._id))
                                .map(material => (
                                  <option key={material._id} value={material._id}>
                                    {material.name} {material.key ? `(${material.key})` : ''}
                                  </option>
                                ))
                              }
                            </select>
                          )}

                          <p className="text-xs text-gray-500 mt-1">
                            Select materials this service applies to. Leave empty to apply to all materials.
                          </p>
                        </div>

                        {/* Promotion Section */}
                        <div className="col-span-2 border-t pt-4 mt-2">
                          <div className="flex items-center mb-3">
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.promotion?.isActive || false}
                                onChange={(e) => updateExtraServicePromotion(index, 'isActive', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm font-medium text-gray-700">Enable Promotion</span>
                            </label>
                          </div>

                          {service.promotion?.isActive && (
                            <div className="grid grid-cols-2 gap-4 bg-blue-50 p-4 rounded-md">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">
                                  Promotional Value
                                  <span className="text-xs text-gray-500 ml-1">(0 = Free/Gift)</span>
                                </label>
                                <input
                                  type="text"
                                  value={service.promotion?.promotionalValue !== null && service.promotion?.promotionalValue !== undefined ? service.promotion.promotionalValue : ''}
                                  onChange={(e) => {
                                    const val = e.target.value.trim();
                                    if (val === '') {
                                      updateExtraServicePromotion(index, 'promotionalValue', null);
                                    } else {
                                      const num = Number(val);
                                      if (!isNaN(num) && num >= 0) {
                                        updateExtraServicePromotion(index, 'promotionalValue', num);
                                      }
                                    }
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                  placeholder="Leave empty for original price"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                                <input
                                  type="datetime-local"
                                  value={service.promotion?.startDate ? service.promotion.startDate.slice(0, 16) : ''}
                                  onChange={(e) => updateExtraServicePromotion(index, 'startDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                              </div>

                              <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                                <input
                                  type="datetime-local"
                                  value={service.promotion?.endDate ? service.promotion.endDate.slice(0, 16) : ''}
                                  onChange={(e) => updateExtraServicePromotion(index, 'endDate', e.target.value ? new Date(e.target.value).toISOString() : null)}
                                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                                />
                              </div>

                              <div className="col-span-2">
                                <I18nField
                                  label="Promotion Description"
                                  value={service.promotion?.description || {}}
                                  onChange={(lang, value) => updateExtraServicePromotionI18n(index, 'description', lang, value)}
                                  languages={supportedLanguages}
                                  maxLength={200}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="col-span-2 flex justify-between items-center">
                          <div className="space-x-4">
                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.enabled}
                                onChange={(e) => updateExtraService(index, 'enabled', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Enabled</span>
                            </label>

                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.isActive}
                                onChange={(e) => updateExtraService(index, 'isActive', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Active</span>
                            </label>

                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.defaultChecked}
                                onChange={(e) => updateExtraService(index, 'defaultChecked', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Default Checked</span>
                            </label>

                            <label className="inline-flex items-center">
                              <input
                                type="checkbox"
                                checked={service.chargeByDefault}
                                onChange={(e) => updateExtraService(index, 'chargeByDefault', e.target.checked)}
                                className="h-4 w-4 text-blue-600 rounded"
                              />
                              <span className="ml-2 text-sm text-gray-700">Charge by Default</span>
                            </label>
                          </div>

                          <button
                            type="button"
                            onClick={() => removeExtraService(index)}
                            className="text-red-600 hover:text-red-800 flex items-center"
                          >
                            <TrashIcon className="h-5 w-5 mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Bulk Discounts Tab */}
          {activeTab === 'discounts' && (
            <div className="space-y-4">
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900">Bulk Discount Settings</h3>
                <div className="grid grid-cols-3 gap-4">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.bulkDiscounts?.enabled ?? true}
                      onChange={(e) => setFormData({
                        ...formData,
                        bulkDiscounts: { ...formData.bulkDiscounts, enabled: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm text-gray-700">Enabled</span>
                  </label>



                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Order Value</label>
                    <input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.bulkDiscounts?.minimumOrderValue || 0}
                      onChange={(e) => setFormData({
                        ...formData,
                        bulkDiscounts: { ...formData.bulkDiscounts, minimumOrderValue: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Discount Tiers</h3>
                <button
                  type="button"
                  onClick={addBulkDiscountTier}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Tier
                </button>
              </div>

              {(formData.bulkDiscounts?.tiers || []).length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No discount tiers defined</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.bulkDiscounts.tiers.map((tier, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Key *
                            <span className="text-xs text-gray-500 ml-1">(unique identifier, max 100 chars)</span>
                          </label>
                          <input
                            type="text"
                            maxLength="100"
                            value={tier.key || ''}
                            onChange={(e) => updateBulkDiscountTier(index, 'key', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="e.g., small_bulk"
                            required
                          />
                        </div>

                        <div className="col-span-2">
                          <I18nField
                            label="Name"
                            value={typeof tier.name === 'object' ? tier.name : {}}
                            onChange={(lang, value) => updateBulkDiscountTierI18n(index, 'name', lang, value)}
                            languages={supportedLanguages}
                            required
                            maxLength={100}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Apply To</label>
                          <select
                            value={tier.applyTo}
                            onChange={(e) => updateBulkDiscountTier(index, 'applyTo', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          >
                            {BULK_APPLY_TO_OPTIONS.map(opt => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Discount % *</label>
                          <input
                            type="number"
                            min="0"
                            max="100"
                            step="0.1"
                            value={tier.discountPercentage}
                            onChange={(e) => updateBulkDiscountTier(index, 'discountPercentage', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Min Quantity *</label>
                          <input
                            type="number"
                            min="1"
                            value={tier.minQuantity}
                            onChange={(e) => updateBulkDiscountTier(index, 'minQuantity', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Max Quantity (leave empty for unlimited)
                          </label>
                          <input
                            type="number"
                            min="1"
                            value={tier.maxQuantity || ''}
                            onChange={(e) => updateBulkDiscountTier(index, 'maxQuantity', e.target.value ? Number(e.target.value) : null)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                          />
                        </div>

                        <div className="col-span-2">
                          <I18nField
                            label="Description"
                            value={typeof tier.description === 'object' ? tier.description : {}}
                            onChange={(lang, value) => updateBulkDiscountTierI18n(index, 'description', lang, value)}
                            languages={supportedLanguages}
                            maxLength={500}
                          />
                        </div>

                        <div className="col-span-2 flex justify-between items-center">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={tier.isActive}
                              onChange={(e) => updateBulkDiscountTier(index, 'isActive', e.target.checked)}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Active</span>
                          </label>

                          <button
                            type="button"
                            onClick={() => removeBulkDiscountTier(index)}
                            className="text-red-600 hover:text-red-800 flex items-center"
                          >
                            <TrashIcon className="h-5 w-5 mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Timeline Settings Tab */}
          {activeTab === 'timeline' && (
            <div className="space-y-4">
              <div className="space-y-4 mb-6">
                <h3 className="text-lg font-medium text-gray-900">Timeline Settings</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Earliest Ready Days *
                      <span className="text-xs text-gray-500 ml-1">(minimum days until order is ready)</span>
                    </label>
                    <input
                      type="number"
                      min="1"
                      value={formData.timelineSettings?.earliestReadyDays || 1}
                      onChange={(e) => setFormData({
                        ...formData,
                        timelineSettings: { ...formData.timelineSettings, earliestReadyDays: Number(e.target.value) }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Apply To</label>
                    <select
                      value={formData.timelineSettings?.applyTo || 'SUBTOTAL'}
                      onChange={(e) => setFormData({
                        ...formData,
                        timelineSettings: { ...formData.timelineSettings, applyTo: e.target.value }
                      })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      {TIMELINE_APPLY_TO_OPTIONS.map(opt => (
                        <option key={opt} value={opt}>{opt}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>

              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">Timeline Tiers</h3>
                <button
                  type="button"
                  onClick={addTimelineTier}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center"
                >
                  <PlusIcon className="h-5 w-5 mr-2" />
                  Add Tier
                </button>
              </div>

              {(formData.timelineSettings?.tiers || []).length === 0 ? (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No timeline tiers defined</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {formData.timelineSettings.tiers.map((tier, index) => (
                    <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Key *
                            <span className="text-xs text-gray-500 ml-1">(unique identifier, max 100 chars)</span>
                          </label>
                          <input
                            type="text"
                            maxLength="100"
                            value={tier.key || ''}
                            onChange={(e) => updateTimelineTier(index, 'key', e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            placeholder="e.g., standard_delivery"
                            required
                          />
                        </div>

                        <div className="col-span-2">
                          <I18nField
                            label="Name"
                            value={typeof tier.name === 'object' ? tier.name : {}}
                            onChange={(lang, value) => updateTimelineTierI18n(index, 'name', lang, value)}
                            languages={supportedLanguages}
                            required
                            maxLength={100}
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">
                            Days After Earliest *
                            <span className="text-xs text-gray-500 ml-1">(0 = rush, higher = slower)</span>
                          </label>
                          <input
                            type="number"
                            min="0"
                            value={tier.daysAfterEarliest ?? 0}
                            onChange={(e) => updateTimelineTier(index, 'daysAfterEarliest', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Fee % *</label>
                          <input
                            type="number"
                            min="0"
                            max="500"
                            step="0.1"
                            value={tier.feePercentage}
                            onChange={(e) => updateTimelineTier(index, 'feePercentage', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Minimum Fee *</label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            value={tier.minimumFee}
                            onChange={(e) => updateTimelineTier(index, 'minimumFee', Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md"
                            required
                          />
                        </div>

                        <div className="col-span-2">
                          <I18nField
                            label="Description"
                            value={typeof tier.description === 'object' ? tier.description : {}}
                            onChange={(lang, value) => updateTimelineTierI18n(index, 'description', lang, value)}
                            languages={supportedLanguages}
                            maxLength={500}
                          />
                        </div>

                        <div className="col-span-2 flex justify-between items-center">
                          <label className="flex items-center">
                            <input
                              type="checkbox"
                              checked={tier.isActive}
                              onChange={(e) => updateTimelineTier(index, 'isActive', e.target.checked)}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Active</span>
                          </label>
                          <label className="flex items-center">
                            <input
                              type="radio"
                              name="timelineTierDefault"
                              checked={tier.isDefault}
                              onChange={(e) => updateTimelineTier(index, 'isDefault', e.target.checked)}
                              className="h-4 w-4 text-blue-600 rounded"
                            />
                            <span className="ml-2 text-sm text-gray-700">Default</span>
                          </label>

                          <button
                            type="button"
                            onClick={() => removeTimelineTier(index)}
                            className="text-red-600 hover:text-red-800 flex items-center"
                          >
                            <TrashIcon className="h-5 w-5 mr-1" />
                            Remove
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Farm Config Tab */}
          {activeTab === 'farm' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Farm Queue Configuration</h3>
              <p className="text-sm text-gray-500">Configure business hours, timezone, and concurrency settings for the print farm queue.</p>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Day Start (HH:mm)</label>
                  <input
                    type="time"
                    value={formData.farmConfig?.businessDayStart || '08:00'}
                    onChange={(e) => setFormData({
                      ...formData,
                      farmConfig: { ...formData.farmConfig, businessDayStart: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Business Day End (HH:mm)</label>
                  <input
                    type="time"
                    value={formData.farmConfig?.businessDayEnd || '18:00'}
                    onChange={(e) => setFormData({
                      ...formData,
                      farmConfig: { ...formData.farmConfig, businessDayEnd: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Concurrency Cap (%)</label>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    value={formData.farmConfig?.concurrencyCapPercentage || 50}
                    onChange={(e) => setFormData({
                      ...formData,
                      farmConfig: { ...formData.farmConfig, concurrencyCapPercentage: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Maximum percentage of farm capacity a single quote can occupy (1-100).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Default Sequence Mode</label>
                  <select
                    value={formData.farmConfig?.defaultSequenceMode || 'BATCH'}
                    onChange={(e) => setFormData({
                      ...formData,
                      farmConfig: { ...formData.farmConfig, defaultSequenceMode: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    {SEQUENCE_MODE_OPTIONS.map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                  <p className="text-xs text-gray-500 mt-1">BATCH groups by model, KIT groups by order.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Timezone</label>
                  <input
                    type="text"
                    value={formData.farmConfig?.timezone || 'Europe/Lisbon'}
                    onChange={(e) => setFormData({
                      ...formData,
                      farmConfig: { ...formData.farmConfig, timezone: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    placeholder="Europe/Lisbon"
                  />
                  <p className="text-xs text-gray-500 mt-1">IANA timezone identifier (e.g. Europe/Lisbon, America/New_York).</p>
                </div>
              </div>
            </div>
          )}

          {/* Packing Tab */}
          {activeTab === 'packing' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Packing Configuration</h3>
              <p className="text-sm text-gray-500">Configure bin-packing parameters for shipping calculation. These override system defaults.</p>

              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Wrapping Loss %</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={formData.packingConfig?.wrappingLossPercent ?? 12}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, wrappingLossPercent: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Volume lost to wrapping material (0-50).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Error Margin %</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={formData.packingConfig?.errorMarginPercent ?? 12}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, errorMarginPercent: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Safety margin for volume estimation errors (0-50).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Weight Safety Margin %</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={formData.packingConfig?.weightSafetyMarginPercent ?? 5}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, weightSafetyMarginPercent: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Weight capacity safety reduction (0-50).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Padding Per Item (mm)</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    value={formData.packingConfig?.paddingPerItemMm ?? 5}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, paddingPerItemMm: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Padding added per side per item in mm (0-50).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Optimization Target</label>
                  <select
                    value={formData.packingConfig?.optimizationTarget || 'lowest_cost'}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, optimizationTarget: e.target.value }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  >
                    <option value="fewest_boxes">Fewest Boxes</option>
                    <option value="lowest_cost">Lowest Cost</option>
                    <option value="balanced">Balanced</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">Box selection strategy for the packing algorithm.</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Tight Fit Threshold</label>
                  <input
                    type="number"
                    min="0.5"
                    max="1"
                    step="0.01"
                    value={formData.packingConfig?.tightFitThreshold ?? 0.88}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, tightFitThreshold: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Volume utilization above which a tight-fit warning triggers (0.5-1.0).</p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Filler Weight (grams)</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.packingConfig?.fillerWeightGrams ?? 75}
                    onChange={(e) => setFormData({
                      ...formData,
                      packingConfig: { ...formData.packingConfig, fillerWeightGrams: Number(e.target.value) }
                    })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  />
                  <p className="text-xs text-gray-500 mt-1">Estimated filler material weight per box in grams.</p>
                </div>

                <div className="flex items-start pt-8">
                  <label className="flex items-center">
                    <input
                      type="checkbox"
                      checked={formData.packingConfig?.allowMixedBoxSizes ?? true}
                      onChange={(e) => setFormData({
                        ...formData,
                        packingConfig: { ...formData.packingConfig, allowMixedBoxSizes: e.target.checked }
                      })}
                      className="h-4 w-4 text-blue-600 rounded"
                    />
                    <span className="ml-2 text-sm font-medium text-gray-700">Allow Mixed Box Sizes</span>
                  </label>
                  <p className="text-xs text-gray-500 ml-6 mt-0.5">Allow different box sizes in one shipment.</p>
                </div>
              </div>
            </div>
          )}

          {/* Farm Stats Tab (read-only) */}
          {activeTab === 'stats' && (
            <div className="space-y-6">
              <h3 className="text-lg font-medium text-gray-900">Farm Stats</h3>
              <p className="text-sm text-gray-500">Auto-calculated metrics based on fleet composition and job history. These fields are read-only.</p>

              {formData.farmStats ? (
                <div className="grid grid-cols-2 gap-6">
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Approx. Monthly Print Hours</p>
                    <p className="text-2xl font-semibold text-gray-900">{formData.farmStats.approxTotalPrintHoursPerMonth ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Approx. Daily Volume (cm³)</p>
                    <p className="text-2xl font-semibold text-gray-900">{formData.farmStats.approxTotalPrintVolumePerDay ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Print Hours</p>
                    <p className="text-2xl font-semibold text-gray-900">{formData.farmStats.totalPrintHours ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Total Failed Hours</p>
                    <p className="text-2xl font-semibold text-red-600">{formData.farmStats.totalFailedHours ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Completed Jobs</p>
                    <p className="text-2xl font-semibold text-green-600">{formData.farmStats.totalDoneJobs ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Failed Jobs</p>
                    <p className="text-2xl font-semibold text-red-600">{formData.farmStats.totalFailedJobs ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Orders Fulfilled</p>
                    <p className="text-2xl font-semibold text-gray-900">{formData.farmStats.totalOrdersFulfilled ?? '-'}</p>
                  </div>
                  <div className="bg-gray-50 rounded-lg p-4">
                    <p className="text-sm text-gray-500">Quality Rating</p>
                    <p className="text-2xl font-semibold text-gray-900">{formData.farmStats.rating ?? 'Not calculated'}</p>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <p className="text-gray-500">No farm stats available yet</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-4">
        <button
          type="submit"
          disabled={saving}
          className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 flex items-center font-medium"
        >
          {saving ? (
            <>
              <ArrowPathIcon className="h-5 w-5 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckIcon className="h-5 w-5 mr-2" />
              Save Settings
            </>
          )}
        </button>
      </div>
    </form>
  );
}
